import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

function RegisterForm({ tenant, membershipTypes }) {
  const stripe = useStripe();
  const elements = useElements();

  const [form, setForm] = useState({ full_name: '', email: '', password: '', confirm_password: '', phone: '', address: '' });
  const [selectedType, setSelectedType] = useState(membershipTypes[0] || null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isFree = !selectedType || parseFloat(selectedType.fee) === 0;

  // Fetch payment intent when type changes (paid types only)
  useEffect(() => {
    if (!selectedType || isFree) {
      setClientSecret(null);
      return;
    }
    setClientSecret(null);
    axios.post('/api/payments/create-intent', {
      type_id: selectedType.type_id,
      tenant_slug: tenant.slug
    })
      .then(res => setClientSecret(res.data.client_secret))
      .catch(err => setError(err.response?.data?.error || 'Could not load payment form.'));
  }, [selectedType, tenant.slug, isFree]);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (e.target.name === 'password' || e.target.name === 'confirm_password') {
      setPasswordError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPasswordError('');

    if (form.password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (form.password !== form.confirm_password) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      let paymentIntentId = null;

      if (!isFree && clientSecret) {
        const result = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: { name: form.full_name, email: form.email }
          }
        });

        if (result.error) {
          setError(result.error.message);
          setLoading(false);
          return;
        }
        paymentIntentId = result.paymentIntent.id;
      }

      const res = await axios.post('/api/members/register', {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        password: form.password,
        type_id: selectedType.type_id,
        tenant_slug: tenant.slug,
        payment_intent_id: paymentIntentId
      });

      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
        <h2 style={{ color: 'var(--primary)', marginBottom: '0.5rem' }}>Welcome!</h2>
        <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>Your membership is confirmed.</p>
        <div style={{ background: '#f9fafb', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.25rem', textAlign: 'left', marginBottom: '1.5rem' }}>
          <div style={{ marginBottom: '0.5rem' }}><strong>Membership Number:</strong> {success.membership_number}</div>
          <div style={{ marginBottom: '0.5rem' }}><strong>Type:</strong> {success.membership_type}</div>
          {success.expiry_date && (
            <div><strong>Valid Until:</strong> {new Date(success.expiry_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
          )}
        </div>
        <a href="/login" className="btn btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Sign in to your account
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Membership type selector */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.5rem' }}>
          Membership Type
        </label>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {membershipTypes.map(type => (
            <label key={type.type_id} style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.875rem 1rem',
              border: `2px solid ${selectedType?.type_id === type.type_id ? 'var(--secondary)' : 'var(--border)'}`,
              borderRadius: '8px', cursor: 'pointer',
              background: selectedType?.type_id === type.type_id ? '#fffbeb' : '#fff'
            }}>
              <input
                type="radio"
                name="type"
                checked={selectedType?.type_id === type.type_id}
                onChange={() => setSelectedType(type)}
                style={{ accentColor: 'var(--secondary)' }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{type.name}</div>
                {type.description && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{type.description}</div>}
              </div>
              <div style={{ fontWeight: 700, color: 'var(--primary)' }}>
                {parseFloat(type.fee) === 0 ? 'Free' : `$${parseFloat(type.fee).toFixed(2)} AUD`}
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label>Full Name</label>
        <input name="full_name" type="text" value={form.full_name} onChange={handleChange} required placeholder="Your full name" />
      </div>
      <div className="form-group">
        <label>Email Address</label>
        <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="you@example.com" />
      </div>
      <div className="form-group">
        <label>Password</label>
        <div style={{ position: 'relative' }}>
          <input
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={form.password}
            onChange={handleChange}
            required
            placeholder="At least 8 characters"
            style={{ paddingRight: '2.75rem' }}
          />
          <button
            type="button"
            onClick={() => setShowPassword(v => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: 0, top: 0, bottom: 0,
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0 0.75rem', color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center',
            }}
          >
            {showPassword ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
        </div>
      </div>
      <div className="form-group">
        <label>Confirm Password</label>
        <div style={{ position: 'relative' }}>
          <input
            name="confirm_password"
            type={showConfirm ? 'text' : 'password'}
            value={form.confirm_password}
            onChange={handleChange}
            required
            placeholder="Repeat your password"
            style={{ paddingRight: '2.75rem' }}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(v => !v)}
            aria-label={showConfirm ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: 0, top: 0, bottom: 0,
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0 0.75rem', color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center',
            }}
          >
            {showConfirm ? (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
        </div>
      </div>
      {passwordError && <div className="error-msg">{passwordError}</div>}
      <div className="form-group">
        <label>Phone</label>
        <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="Optional" />
      </div>
      <div className="form-group">
        <label>Address</label>
        <input name="address" type="text" value={form.address} onChange={handleChange} placeholder="Optional" />
      </div>

      {!isFree && (
        <div className="form-group">
          <label>Card Details</label>
          <div style={{ padding: '0.75rem 1rem', border: '1.5px solid var(--border)', borderRadius: '8px' }}>
            <CardElement options={{ style: { base: { fontSize: '15px', color: '#1a1a2e' } } }} />
          </div>
          {!clientSecret && (
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>Loading payment form...</p>
          )}
        </div>
      )}

      {error && <div className="error-msg">{error}</div>}

      <button
        type="submit"
        className="btn btn-primary"
        disabled={loading || (!isFree && !clientSecret)}
        style={{ marginTop: '0.5rem' }}
      >
        {loading ? 'Processing...' : isFree ? 'Complete Registration' : `Pay & Register`}
      </button>
    </form>
  );
}

function Register() {
  const { tenant_slug } = useParams();
  const [info, setInfo] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`/api/members/registration-info/${tenant_slug}`)
      .then(res => {
        setInfo(res.data);
        if (res.data.tenant.stripe_publishable_key) {
          setStripePromise(loadStripe(res.data.tenant.stripe_publishable_key));
        }
      })
      .catch(() => setError('Registration is not available for this organisation.'));
  }, [tenant_slug]);

  const banner = (
    <>
      <div className="login-banner">
        <img src="/scala-logo.png" alt="SCALA logo" className="login-banner-logo" />
        <div>
          <div className="login-banner-title">SCALA Membership</div>
          <div className="login-banner-tagline" style={{ fontVariant: 'small-caps' }}>Membership Registration</div>
        </div>
      </div>
      <div className="login-gold-divider" />
    </>
  );

  if (error) {
    return (
      <div className="auth-page">
        <div className="login-wrapper">
          {banner}
          <div className="login-card" style={{ textAlign: 'center' }}>
            <p className="error-msg">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="auth-page">
        <div className="login-wrapper">
          {banner}
          <div className="login-card" style={{ textAlign: 'center' }}>
            <p style={{ color: '#6b7280' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  const { tenant, membershipTypes } = info;

  const content = (
    <div className="auth-page">
      <div className="login-wrapper">

        {/* Top banner */}
        {banner}

        {/* White card */}
        <div className="login-card" style={{ maxWidth: '520px' }}>
          <div style={{ marginBottom: '1.25rem' }}>
            <Link to="/login" style={{ fontSize: '0.875rem', color: '#6b7280', textDecoration: 'none' }}>
              ← Back to login
            </Link>
          </div>

          {membershipTypes.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#6b7280' }}>No membership types are currently available.</p>
          ) : (
            stripePromise ? (
              <Elements stripe={stripePromise}>
                <RegisterForm tenant={tenant} membershipTypes={membershipTypes} />
              </Elements>
            ) : (
              <RegisterForm tenant={tenant} membershipTypes={membershipTypes} />
            )
          )}
        </div>

      </div>
    </div>
  );

  return content;
}

export default Register;
